/**
 * MatrixRow — one registry metric across the period columns: the metric name opens
 * its DefinitionPopover and a trend affordance opens the A2 chart (drill stays on the
 * cell value); each period renders the Actual MetricCell with its Target beneath.
 * Typed props only (CD-7) — cells arrive as Cell lookups, never numbers.
 */

import React from 'react'
import clsx from 'clsx'
import { TrendingUp } from 'lucide-react'
import type { Cell, CellLineage, PlanType, RegistryMetric } from '../../types/metrics'
import DefinitionPopover from './DefinitionPopover'
import MetricCell from './MetricCell'
import TargetOnlyCell from './TargetOnlyCell'
import { EM_DASH, formatCellYtd, periodHeading } from './cellFormat'
import { ytdFor, type YtdLeg } from './ytdColumn'

export type CellLookup = (metricId: string, period: string, plan: PlanType) => Cell | null

interface MatrixRowProps {
  metric: RegistryMetric
  periods: string[]
  cellFor: CellLookup
  /** Monthly only — the weekly master carries no YTD, so the column would be all dashes. */
  showYtd: boolean
  onDrill?: (cell: Cell) => void // S3: threaded to MetricCell's drill affordance
  onTrend?: (metricId: string) => void // A2: opens the metric's trend chart
}

function RowHeaderCell({
  metric,
  lineage,
  onTrend,
}: {
  metric: RegistryMetric
  lineage: CellLineage | null
  onTrend?: (metricId: string) => void
}): React.JSX.Element {
  return (
    <th scope="row" className="sticky left-0 z-[1] bg-panel px-3 py-1.5 text-left font-normal whitespace-nowrap">
      <div className="flex items-center gap-1.5">
        <DefinitionPopover metric={metric} lineage={lineage} />
        {onTrend && (
          <button
            type="button"
            data-testid={`trend-btn-${metric.id}`}
            onClick={() => onTrend(metric.id)}
            aria-label={`Trend ${metric.title}`}
            className="rounded p-0.5 text-mut2 hover:bg-panel2 hover:text-ink"
          >
            <TrendingUp size={12} />
          </button>
        )}
      </div>
    </th>
  )
}

/** One period's cell. Three states, and the middle one is why this exists: a served
 *  target with no actual yet (every future month) must keep its plan, not collapse to
 *  the not-served dash — see TargetOnlyCell. */
function PeriodCell({
  metric,
  period,
  cellFor,
  onDrill,
}: {
  metric: RegistryMetric
  period: string
  cellFor: CellLookup
  onDrill?: (cell: Cell) => void
}): React.JSX.Element {
  const actual = cellFor(metric.id, period, 'Actual')
  const target = cellFor(metric.id, period, 'Target')
  const clickable = Boolean(actual?.drill.enabled && onDrill)
  return (
    <td
      data-testid={`cell-${metric.id}-${period}`}
      className={clsx('align-top border-l border-line2', clickable && 'cx-cell')}
    >
      {actual ? (
        <MetricCell cell={actual} target={target} onDrill={onDrill} />
      ) : target ? (
        <TargetOnlyCell target={target} />
      ) : (
        // honest not-served state: no row in the master for this period
        <span className="block px-3 py-2 text-right text-xs text-mut2">{EM_DASH}</span>
      )}
    </td>
  )
}

/** The year-to-date readout. NO RAG and no arrow, deliberately: the two legs do not always
 *  run to the same month (Past Due's actuals stop in June while its plan reaches July), so a
 *  verdict on the pair would sometimes be a verdict on two different windows. The client's
 *  report does not colour this column either. What it does not say, and this does, is WHICH
 *  month each leg runs to — on the title, where a reader who needs it can find it. */
function YtdCell({ metric, periods, cellFor }: Omit<MatrixRowProps, 'onDrill' | 'onTrend'>): React.JSX.Element {
  const { actual, target, comparable } = ytdFor(metric.id, periods, cellFor)
  const asAt = (leg: YtdLeg | null) => (leg ? periodHeading(leg.period) : null)
  const title = [
    actual && `Year to date at ${asAt(actual)}`,
    target && `plan to ${asAt(target)}`,
    actual && target && !comparable && 'different windows — not a like-for-like pair',
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <td
      data-testid={`ytd-${metric.id}`}
      className="align-top border-l border-line bg-bg2/30"
      title={title || undefined}
    >
      <div className="px-3 py-2 text-right whitespace-nowrap">
        <span data-testid={`ytd-value-${metric.id}`} className="block text-xs font-semibold tabular-nums text-ink">
          {actual ? formatCellYtd(actual.cell) : EM_DASH}
        </span>
        <span className="mt-0.5 block font-mono text-[10px] text-mut2">
          {target ? `tgt ${formatCellYtd(target.cell)}` : EM_DASH}
        </span>
      </div>
    </td>
  )
}

export default function MatrixRow({
  metric,
  periods,
  cellFor,
  showYtd,
  onDrill,
  onTrend,
}: MatrixRowProps): React.JSX.Element {
  // Lineage for the row header: the first cell served at all. Actual is preferred within a
  // period, but a row that is all plan (a future-only metric) still knows its source view.
  const firstServed = periods
    .flatMap((period) => [cellFor(metric.id, period, 'Actual'), cellFor(metric.id, period, 'Target')])
    .find((cell) => cell !== null)
  return (
    <tr className="cx-row border-b border-line2 last:border-b-0">
      <RowHeaderCell metric={metric} lineage={firstServed?.lineage ?? null} onTrend={onTrend} />
      {periods.map((period) => (
        <PeriodCell key={period} metric={metric} period={period} cellFor={cellFor} onDrill={onDrill} />
      ))}
      {showYtd && <YtdCell metric={metric} periods={periods} cellFor={cellFor} showYtd={showYtd} />}
    </tr>
  )
}
